import React from "react";
import { Check, Shield, Droplets } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Field = ({ label, htmlFor, children }) => (
  <div>
    <Label htmlFor={htmlFor} className="text-xs tracking-[0.18em] uppercase text-white/55 mb-2 block">
      {label}
    </Label>
    {children}
  </div>
);

export const ContactFields = ({ form, update }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    <Field label="Full name" htmlFor="name">
      <Input id="name" data-testid="booking-name" value={form.name} onChange={update("name")} placeholder="Jamie Smith" className="bookingInput" />
    </Field>
    <Field label="Phone" htmlFor="phone">
      <Input id="phone" data-testid="booking-phone" value={form.phone} onChange={update("phone")} placeholder="07000 000000" className="bookingInput" />
    </Field>
    <Field label="Email" htmlFor="email">
      <Input id="email" type="email" data-testid="booking-email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="bookingInput" />
    </Field>
    <Field label="Vehicle (make & model)" htmlFor="vehicle">
      <Input id="vehicle" data-testid="booking-vehicle" value={form.vehicle} onChange={update("vehicle")} placeholder="BMW M3 — Black" className="bookingInput" />
    </Field>
  </div>
);

export const PackageSelect = ({ form, setForm, services, selectedPackage }) => (
  <Field label="Package">
    <Select value={form.package_id} onValueChange={(v) => setForm((f) => ({ ...f, package_id: v }))}>
      <SelectTrigger data-testid="booking-package-trigger" className="bookingInput h-12">
        <SelectValue placeholder="Choose a package" />
      </SelectTrigger>
      <SelectContent className="bg-[#0E0E0E] border-white/15 text-white">
        {services.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`booking-package-${s.id}`}>
            {s.name} — £{s.price}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {selectedPackage && (
      <p className="mt-2 text-xs text-white/45">
        {selectedPackage.tagline} · {selectedPackage.duration}
      </p>
    )}
  </Field>
);

export const LocationField = ({ form, update }) => (
  <Field label="Service location (postcode / address)" htmlFor="location">
    <Input id="location" data-testid="booking-location" value={form.location} onChange={update("location")} placeholder="B1 1AA · 12 Example Road" className="bookingInput" />
  </Field>
);

export const AddonPicker = ({ addons, form, setForm }) => {
  if (addons.length === 0) return null;
  return (
    <div data-testid="booking-addons">
      <Label className="text-xs tracking-[0.18em] uppercase text-white/55 mb-3 block">
        Add-ons (paid on the day)
      </Label>
      <div className="space-y-2">
        {addons.map((a) => {
          const selected = form.addons.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              data-testid={`booking-addon-${a.id}`}
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  addons: selected
                    ? f.addons.filter((x) => x !== a.id)
                    : [...f.addons, a.id],
                }))
              }
              className={`w-full text-left p-4 border transition-colors flex items-start gap-3 ${
                selected ? "border-white/55 bg-white/5" : "border-white/12 hover:border-white/30"
              }`}
            >
              <span
                className={`mt-0.5 inline-flex w-4 h-4 items-center justify-center border ${
                  selected ? "bg-white border-white" : "border-white/35"
                }`}
              >
                {selected && <Check size={11} className="text-black" />}
              </span>
              <span className="flex-1">
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-white">{a.name}</span>
                  <span className="text-sm text-white/70 whitespace-nowrap">+£{a.price}</span>
                </span>
                <span className="block text-xs text-white/50 mt-1 leading-snug">
                  {a.description} · {a.duration}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const NotesField = ({ form, update }) => (
  <Field label="Notes (optional)" htmlFor="notes">
    <Textarea
      id="notes"
      data-testid="booking-notes"
      value={form.notes}
      onChange={update("notes")}
      rows={3}
      placeholder="Anything we should know — pet hair, heavy mud, dog cage, etc."
      className="bookingInput resize-none"
    />
  </Field>
);

export const ReferredByField = ({ form, update }) => (
  <Field label="Referred by a friend? (their phone or email — optional)" htmlFor="referred_by">
    <Input
      id="referred_by"
      data-testid="booking-referred-by"
      value={form.referred_by}
      onChange={update("referred_by")}
      placeholder="07000 000000 or friend@email.com"
      className="bookingInput"
    />
    <p className="mt-2 text-xs text-white/45">
      They&apos;ll get £10 off their next wash once your booking is confirmed.
    </p>
  </Field>
);

export const InfoBoxes = ({ deposit }) => (
  <>
    <div className="p-5 border border-white/10 bg-[#0A0A0A] flex items-start gap-3" data-testid="water-power-info">
      <Droplets size={16} className="mt-0.5 text-white/55 flex-shrink-0" />
      <div className="text-sm text-white/70 leading-relaxed">
        <div className="text-white">We use your outdoor tap & a power socket</div>
        <div className="text-white/55 text-xs mt-1">
          Please make sure you have an accessible outside tap and a nearby power socket on the day. We bring everything else — hoses, extension leads, products, towels. Self-contained water tank coming soon.
        </div>
      </div>
    </div>

    <div className="p-5 border border-white/10 bg-[#0A0A0A] flex items-start gap-3" data-testid="deposit-info">
      <Shield size={16} className="mt-0.5 text-white/55 flex-shrink-0" />
      <div className="text-sm text-white/70 leading-relaxed">
        <div className="text-white">£{deposit.amount} refundable deposit</div>
        <div className="text-white/55 text-xs mt-1">
          Paid securely via Stripe. Fully refunded if you cancel 24 hrs or more before the booking. Balance is paid in person on the day.
        </div>
      </div>
    </div>
  </>
);
